import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  Users,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  Clock,
  AlertTriangle,
  Check,
  X,
  User,
  Briefcase,
  MapPin,
  MoreHorizontal,
  Palette,
  Filter,
  CalendarDays,
  LayoutGrid,
  ExternalLink,
  BookOpen,
  UserCheck,
  GripVertical,
  Move,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const ROLES = ["Interiors", "Exteriors", "Animation", "Generalist"];
const EMPLOYMENT_TYPES = ["Employee", "Contractor", "Freelancer"];
const STATUSES = ["Active", "On Leave", "Inactive"];
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_OFF_TYPES = ["PTO", "Sick", "Personal", "Holiday", "Other"];
const BLOCK_STATUSES = ["Planned", "Confirmed", "In Progress", "On Hold"];

const WORK_TYPE_COLORS = {
  "Interior": "#3D6355",
  "Exterior": "#2E7D55",
  "Animation": "#3D6355",
  "Revisions": "#B96A43",
  "Buffer": "#565350",
  "QA": "#3D6355",
  "Internal": "#2E7D55",
  "3D Model": "#B96A43",
  "VEXOS": "#3D6355"
};

const COLOR_MODES = [
  { id: "workType", label: "By Work Type" },
  { id: "pm", label: "By Program Manager" },
  { id: "artist", label: "By Artist" },
  { id: "project", label: "By Project" }
];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateFull(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function getWeekDates(date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);  // Normalize to midnight
  start.setDate(start.getDate() - start.getDay() + 1); // Monday
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);  // Ensure midnight
    dates.push(d);
  }
  return dates;
}

function getMonthDates(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  firstDay.setHours(0, 0, 0, 0);  // Normalize to midnight
  const lastDay = new Date(year, month + 1, 0);
  lastDay.setHours(0, 0, 0, 0);  // Normalize to midnight
  const dates = [];
  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
    const newDate = new Date(d);
    newDate.setHours(0, 0, 0, 0);  // Ensure midnight
    dates.push(newDate);
  }
  return dates;
}

function isSameDay(d1, d2) {
  return d1.toDateString() === d2.toDateString();
}

// Check if a date is a weekend (Saturday = 6, Sunday = 0)
function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

// Get the next working day (skip weekends)
function getNextWorkingDay(date) {
  const d = new Date(date);
  while (isWeekend(d)) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

// Get the previous working day (skip weekends)
function getPrevWorkingDay(date) {
  const d = new Date(date);
  while (isWeekend(d)) {
    d.setDate(d.getDate() - 1);
  }
  return d;
}

// Count working days between two dates (excluding weekends)
function countWorkingDays(startDate, endDate) {
  let count = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    if (!isWeekend(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
}

// Add working days to a date (skipping weekends)
function addWorkingDays(startDate, numDays) {
  const result = new Date(startDate);
  let daysAdded = 0;
  
  // If starting on weekend, move to next weekday first
  while (isWeekend(result)) {
    result.setDate(result.getDate() + 1);
  }
  
  // Add the working days
  while (daysAdded < numDays - 1) { // -1 because we count the start day
    result.setDate(result.getDate() + 1);
    if (!isWeekend(result)) {
      daysAdded++;
    }
  }
  
  return result;
}

// Subtract working days from a date (skipping weekends)
function subtractWorkingDays(endDate, numDays) {
  const result = new Date(endDate);
  let daysSubtracted = 0;
  
  // If ending on weekend, move to previous weekday first
  while (isWeekend(result)) {
    result.setDate(result.getDate() - 1);
  }
  
  // Subtract the working days
  while (daysSubtracted < numDays - 1) { // -1 because we count the end day
    result.setDate(result.getDate() - 1);
    if (!isWeekend(result)) {
      daysSubtracted++;
    }
  }
  
  return result;
}

// Split a block into weekday-only segments (visual gaps for weekends)
// This is the SINGLE SOURCE OF TRUTH for business-day segmentation across all views
// Workdays = Mon-Fri. Weekends (Sat/Sun) are ALWAYS excluded from visual rendering.
function splitBlockByWeekends(startDate, endDate, viewDates) {
  const segments = [];
  if (!startDate || !endDate || !viewDates || viewDates.length === 0) return segments;
  
  // Clone dates to avoid mutation
  const blockStart = new Date(startDate);
  const blockEnd = new Date(endDate);
  blockStart.setHours(0, 0, 0, 0);
  blockEnd.setHours(0, 0, 0, 0);
  
  // Get view range
  const viewStart = new Date(viewDates[0]);
  const viewEnd = new Date(viewDates[viewDates.length - 1]);
  viewStart.setHours(0, 0, 0, 0);
  viewEnd.setHours(0, 0, 0, 0);
  
  // If block is entirely outside view range, return empty
  if (blockEnd < viewStart || blockStart > viewEnd) return segments;
  
  // Determine the effective start and end within the view
  const effectiveStart = new Date(Math.max(blockStart.getTime(), viewStart.getTime()));
  const effectiveEnd = new Date(Math.min(blockEnd.getTime(), viewEnd.getTime()));
  
  // Current position pointer
  let current = new Date(effectiveStart);
  
  // Safety counter to prevent infinite loops
  let iterations = 0;
  const maxIterations = 100;
  
  while (current <= effectiveEnd && iterations < maxIterations) {
    iterations++;
    
    // Skip weekends - find first weekday
    while (current <= effectiveEnd && isWeekend(current)) {
      current.setDate(current.getDate() + 1);
    }
    
    // If we've gone past the effective end after skipping weekends, done
    if (current > effectiveEnd) break;
    
    // Start a new segment on this weekday
    const segmentStart = new Date(current);
    let segmentEnd = new Date(current);
    
    // Extend the segment through consecutive weekdays until we hit a weekend or the effective end
    while (segmentEnd < effectiveEnd) {
      const nextDay = new Date(segmentEnd);
      nextDay.setDate(nextDay.getDate() + 1);
      
      // If next day is past effective end, stop here
      if (nextDay > effectiveEnd) break;
      
      // If next day is a weekend, stop the segment on the current weekday (Friday)
      if (isWeekend(nextDay)) break;
      
      // Next day is a weekday within range, extend segment
      segmentEnd = new Date(nextDay);
    }
    
    // Add the segment (only weekdays)
    segments.push({
      start: new Date(segmentStart),
      end: new Date(segmentEnd)
    });
    
    // Move current to the day after segment end to find the next segment
    current = new Date(segmentEnd);
    current.setDate(current.getDate() + 1);
  }
  
  return segments;
}

// Parse "YYYY-MM-DD" string as local date (not UTC)
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function isDateInRange(date, startStr, endStr) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr);
  if (!start || !end) return false;
  return d >= start && d <= end;
}

// Generate consistent colors for artists/projects
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "#3D6355", "#2E7D55", "#3D6355", "#B96A43", "#3D6355", 
    "#2E7D55", "#C24A2E", "#3D6355", "#2E7D55", "#B96A43"
  ];
  return colors[Math.abs(hash) % colors.length];
}

// Block Tooltip Content - Apple-like design with rich details
function BlockTooltipContent({ block, artistName, workingDays, totalHours }) {
  const displayTitle = block.program_id && block.program_name 
    ? `${block.program_name} — ${block.work_type}`
    : block.title;
  
  return (
    <div className="min-w-[240px] max-w-[320px] p-3 space-y-2.5 text-left">
      {/* Title */}
      <div className="font-semibold text-sm text-white leading-tight">
        {displayTitle}
      </div>
      
      {/* Work Type Badge */}
      <div className="flex items-center gap-2">
        <div 
          className="w-2.5 h-2.5 rounded-full" 
          style={{ backgroundColor: WORK_TYPE_COLORS[block.work_type] || '#3D6355' }} 
        />
        <span className="text-xs text-white/80">{block.work_type}</span>
        {block.status && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/20 text-white/90">
            {block.status}
          </span>
        )}
        {block.origin === "archviz-planner" && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(167,139,250,0.25)", color: "#ADBDB2" }}>
            ArchViz Planner
          </span>
        )}
      </div>
      
      {/* Artist */}
      {artistName && (
        <div className="flex items-center gap-2 text-xs text-white/70">
          <User className="w-3 h-3" />
          <span>{artistName}</span>
        </div>
      )}
      
      {/* Date Range */}
      <div className="flex items-center gap-2 text-xs text-white/70">
        <Calendar className="w-3 h-3" />
        <span>{formatDate(block.start_date)} → {formatDate(block.end_date)}</span>
      </div>
      
      {/* Hours */}
      <div className="flex items-center gap-2 text-xs text-white/70">
        <Clock className="w-3 h-3" />
        <span>
          {block.hours_type === "half" ? "4h" : block.hours_type === "custom" ? `${block.hours_per_day || 8}h` : "8h"}/day
          {workingDays > 0 && totalHours > 0 && (
            <span className="ml-1 text-white/50">
              • {workingDays} days • {totalHours}h total
            </span>
          )}
        </span>
      </div>
      
      {/* Notes Preview */}
      {block.notes && (
        <div className="pt-1.5 border-t border-white/10">
          <p className="text-[11px] text-white/60 leading-relaxed line-clamp-3">
            {block.notes.length > 120 ? block.notes.substring(0, 120) + '…' : block.notes}
          </p>
        </div>
      )}
      
      {/* Double-booking Warning */}
      {block.hasOverlap && (
        <div className="flex items-center gap-1.5 pt-1 text-amber-400 text-[11px]">
          <AlertTriangle className="w-3 h-3" />
          <span>Double-booked</span>
        </div>
      )}
    </div>
  );
}

// Sortable Artist Row component for drag and drop reordering
function SortableArtistRow({ artist, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: artist.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className="flex border-b border-border last:border-b-0 hover:bg-secondary/10 group bg-transparent">
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="w-6 shrink-0 flex items-center justify-center cursor-grab active:cursor-grabbing bg-muted/30 hover:bg-muted/50 opacity-0 group-hover:opacity-100 transition-opacity border-r border-border"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      {children}
    </div>
  );
}

export default function ArtistManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialArtistFilter = searchParams.get("artist");
  
  const [artists, setArtists] = useState([]);
  const [artistOrder, setArtistOrder] = useState(() => {
    // Load saved order from localStorage
    const saved = localStorage.getItem('artistTimelineOrder');
    return saved ? JSON.parse(saved) : [];
  });
  const [assignments, setAssignments] = useState([]);
  const [timeOffs, setTimeOffs] = useState([]);
  const [otEntries, setOtEntries] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  
  // View state
  const [viewMode, setViewMode] = useState("week"); // day, week, month
  const [currentDate, setCurrentDate] = useState(new Date());
  const [colorMode, setColorMode] = useState("workType");
  const [selectedArtist, setSelectedArtist] = useState(initialArtistFilter || null);
  const [showFreeOnly, setShowFreeOnly] = useState(false);  // Free Artists filter
  
  // Dialogs
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showTimeOff, setShowTimeOff] = useState(false);
  const [editingTimeOff, setEditingTimeOff] = useState(null);
  const [showAddOT, setShowAddOT] = useState(false);
  const [showOTList, setShowOTList] = useState(false);
  const [loadingOT, setLoadingOT] = useState(false);
  const [editingOT, setEditingOT] = useState(null);
  
  // Drag and drop state
  const [draggingBlock, setDraggingBlock] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [dragOverArtist, setDragOverArtist] = useState(null);
  
  // Resize state
  const [resizingBlock, setResizingBlock] = useState(null);
  const [resizeEdge, setResizeEdge] = useState(null); // "start" or "end"
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeOriginalBlock, setResizeOriginalBlock] = useState(null);
  const timelineRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  
  // Refs for resize handlers (to avoid stale closures)
  const resizeStateRef = useRef({
    block: null,
    edge: null,
    startX: 0,
    originalBlock: null
  });
  
  // Measure scrollbar width for header/body alignment
  useEffect(() => {
    const measureScrollbar = () => {
      if (bodyScrollRef.current) {
        const width = bodyScrollRef.current.offsetWidth - bodyScrollRef.current.clientWidth;
        setScrollbarWidth(width);
      }
    };
    // Small delay to ensure DOM is rendered
    const timeoutId = setTimeout(measureScrollbar, 100);
    window.addEventListener('resize', measureScrollbar);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', measureScrollbar);
    };
  }, [artists, viewMode]); // Re-measure when artists change or view mode changes
  
  // Form data  
  const [blockForm, setBlockForm] = useState({
    artist_id: "", program_id: "", custom_label: "", work_type: "Interior",
    start_date: "", end_date: "", status: "Planned", notes: "", color: "",
    hours_type: "full", hours_per_day: 8,
    planner_item_id: "", planner_item_type: "", planner_item_label: ""
  });
  
  // Planner items state for drill-down
  const [plannerItems, setPlannerItems] = useState({ has_planner: false, items: [] });
  const [loadingPlannerItems, setLoadingPlannerItems] = useState(false);
  
  const [timeOffForm, setTimeOffForm] = useState({
    artist_id: "", type: "PTO", start_date: "", end_date: "", notes: ""
  });
  
  // OT Form state
  const [otForm, setOtForm] = useState({
    artist_id: "", program_id: "", deliverable_id: "", deliverable_type: "",
    deliverable_label: "", date: "", hours: "", rate: 15, notes: ""
  });
  const [otPlannerItems, setOtPlannerItems] = useState([]);
  const [loadingOtPlannerItems, setLoadingOtPlannerItems] = useState(false);

  // Fetch OT entries
  async function fetchOTEntries() {
    setLoadingOT(true);
    try {
      const res = await apiClient.get("/ot-entries");
      setOtEntries(res.data || []);
    } catch (err) {
      console.error("Failed to fetch OT entries:", err);
    } finally {
      setLoadingOT(false);
    }
  }

  // Create OT entry
  async function handleCreateOT(e) {
    e.preventDefault();
    if (!otForm.artist_id || !otForm.date || !otForm.hours) {
      toast.error("Please fill in required fields");
      return;
    }
    try {
      await apiClient.post("/ot-entries", {
        artist_id: otForm.artist_id,
        program_id: otForm.program_id || null,
        deliverable_id: otForm.deliverable_id || null,
        deliverable_type: otForm.deliverable_type || null,
        deliverable_label: otForm.deliverable_label || null,
        date: otForm.date,
        hours: parseFloat(otForm.hours),
        rate: parseFloat(otForm.rate) || 15,
        notes: otForm.notes || null
      });
      toast.success("OT entry created and logged to project costing");
      setShowAddOT(false);
      setOtForm({
        artist_id: "", program_id: "", deliverable_id: "", deliverable_type: "",
        deliverable_label: "", date: "", hours: "", rate: 15, notes: ""
      });
      setOtPlannerItems([]);
      fetchOTEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create OT entry");
    }
  }

  // Update OT entry
  async function handleUpdateOT(entryId, updates) {
    try {
      await apiClient.patch(`/ot-entries/${entryId}`, updates);
      toast.success("OT entry updated");
      fetchOTEntries();
      setEditingOT(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update OT entry");
    }
  }

  // Delete OT entry
  async function handleDeleteOT(entryId) {
    if (!confirm("Delete this OT entry? This will also remove it from project costing.")) return;
    try {
      await apiClient.delete(`/ot-entries/${entryId}`);
      toast.success("OT entry deleted");
      fetchOTEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete OT entry");
    }
  }

  // Fetch planner items for OT (same logic as Add Block)
  async function fetchOtPlannerItems(programId) {
    if (!programId) {
      setOtPlannerItems([]);
      return;
    }
    setLoadingOtPlannerItems(true);
    try {
      const res = await apiClient.get(`/production-planner/programs/${programId}/items`);
      setOtPlannerItems(res.data || []);
    } catch (err) {
      console.error("Failed to fetch planner items for OT:", err);
      setOtPlannerItems([]);
    } finally {
      setLoadingOtPlannerItems(false);
    }
  }

  // Sync leaves to timeline (automatic on load + manual button)
  async function syncLeavesToTimeline(showToast = false) {
    try {
      setSyncing(true);
      const res = await apiClient.post("/leaves/sync-to-timeline");
      setLastSyncResult(res.data);
      
      if (res.data.synced > 0) {
        // Refresh time-off data if any were synced
        const timeOffRes = await apiClient.get("/time-off");
        setTimeOffs(timeOffRes.data || []);
        if (showToast) {
          toast.success(`Synced ${res.data.synced} leave(s) to timeline`);
        }
      } else if (showToast) {
        toast.info("All leaves already synced");
      }
      
      return res.data;
    } catch (err) {
      console.error("Failed to sync leaves:", err);
      if (showToast) {
        toast.error("Failed to sync leaves");
      }
      return null;
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);
  
  // Auto-sync leaves on initial load
  useEffect(() => {
    if (!loading && artists.length > 0) {
      // Run sync silently on load
      syncLeavesToTimeline(false);
    }
  }, [loading]);

  async function fetchData() {
    try {
      // Fetch each endpoint separately with individual error handling
      let artistsData = [];
      let assignmentsData = [];
      let timeOffsData = [];
      let workTypesData = [];
      let programsData = [];
      let otData = [];
      let holidaysData = [];
      
      try {
        const artistsRes = await apiClient.get("/artists");
        artistsData = artistsRes.data || [];
      } catch (err) {
        console.error("Failed to fetch artists:", err.response?.data || err.message);
        toast.error("Failed to load artists");
      }
      
      try {
        const assignmentsRes = await apiClient.get("/assignment-blocks");
        assignmentsData = assignmentsRes.data || [];
      } catch (err) {
        console.error("Failed to fetch assignment blocks:", err.response?.data || err.message);
        toast.error("Failed to load assignment blocks");
      }
      
      try {
        const timeOffRes = await apiClient.get("/time-off");
        timeOffsData = timeOffRes.data || [];
      } catch (err) {
        console.error("Failed to fetch time-off:", err.response?.data || err.message);
      }
      
      try {
        const workTypesRes = await apiClient.get("/work-types");
        workTypesData = workTypesRes.data || [];
      } catch (err) {
        console.error("Failed to fetch work types:", err.response?.data || err.message);
      }
      
      try {
        const programsRes = await apiClient.get("/programs");
        programsData = programsRes.data || [];
      } catch (err) {
        console.error("Failed to fetch programs:", err.response?.data || err.message);
      }
      
      try {
        const otRes = await apiClient.get("/ot-entries");
        otData = (otRes.data || []).filter(e => e.status === "Approved");
      } catch (err) {
        // Silent fail for OT entries
      }
      
      try {
        const holidaysRes = await apiClient.get("/holidays");
        holidaysData = holidaysRes.data || [];
      } catch (err) {
        // Silent fail for holidays
      }
      
      setArtists(artistsData);
      setAssignments(assignmentsData);
      setTimeOffs(timeOffsData);
      setWorkTypes(workTypesData);
      setPrograms(programsData);
      setOtEntries(otData);
      setHolidays(holidaysData);
    } catch (error) {
      console.error("Unexpected error in fetchData:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  // Helper to get artist display name (handles old/new field names)
  function getArtistDisplayName(artist) {
    return artist.display_name || artist.preferred_name || artist.legal_name || artist.full_name || "Unknown";
  }

  // Assignment Block CRUD
  async function handleSaveBlock() {
    // Validation: require artist, dates, and either a project or custom label
    if (!blockForm.artist_id || !blockForm.start_date || !blockForm.end_date) {
      toast.error("Please fill required fields");
      return;
    }
    
    // If no project linked, require custom label
    if (!blockForm.program_id && !blockForm.custom_label?.trim()) {
      toast.error("Please select a project or enter a custom label");
      return;
    }
    
    // Custom label max 60 chars
    if (blockForm.custom_label && blockForm.custom_label.length > 60) {
      toast.error("Custom label must be 60 characters or less");
      return;
    }
    
    // Validate and adjust dates to exclude weekends
    let startDate = parseLocalDate(blockForm.start_date);
    let endDate = parseLocalDate(blockForm.end_date);
    
    // If start is on weekend, move to next weekday
    if (isWeekend(startDate)) {
      startDate = getNextWorkingDay(startDate);
      toast.info("Start date adjusted to skip weekend");
    }
    
    // If end is on weekend, move to previous weekday
    if (isWeekend(endDate)) {
      endDate = getPrevWorkingDay(endDate);
      toast.info("End date adjusted to skip weekend");
    }
    
    // Ensure end is not before start after adjustments
    if (endDate < startDate) {
      endDate = new Date(startDate);
    }
    
    // Generate title from project + work type, or use custom label
    const selectedProgram = programs.find(p => p.id === blockForm.program_id);
    const generatedTitle = selectedProgram 
      ? `${selectedProgram.program_name} — ${blockForm.work_type}`
      : blockForm.custom_label?.trim();
    
    const adjustedForm = {
      ...blockForm,
      title: generatedTitle,
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0]
    };
    
    try {
      if (editingBlock) {
        const res = await apiClient.patch(`/assignment-blocks/${editingBlock.id}`, adjustedForm);
        setAssignments(assignments.map(a => a.id === editingBlock.id ? res.data : a));
        toast.success("Block updated");
      } else {
        const res = await apiClient.post("/assignment-blocks", adjustedForm);
        setAssignments([...assignments, res.data]);
        toast.success("Block created");
      }
      resetBlockForm();
    } catch (error) {
      toast.error("Failed to save block");
    }
  }

  async function handleDeleteBlock(id) {
    try {
      const deletedBlock = assignments.find(a => a.id === id);
      await apiClient.delete(`/assignment-blocks/${id}`);
      setAssignments(assignments.filter(a => a.id !== id));
      // Clear the assignee on the originating planner phase, if this came from the planner.
      syncPlannerBack(deletedBlock, { deleted: true });
      toast.success("Block deleted");
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to delete block";
      toast.error(detail);
      console.error("Delete block error:", error.response?.data || error.message);
    }
  }

  function resetBlockForm() {
    setBlockForm({
      artist_id: "", program_id: "", custom_label: "", work_type: "Interior",
      start_date: "", end_date: "", status: "Planned", notes: "", color: "",
      hours_type: "full", hours_per_day: 8,
      planner_item_id: "", planner_item_type: "", planner_item_label: ""
    });
    setPlannerItems({ has_planner: false, items: [] });
    setShowAddBlock(false);
    setEditingBlock(null);
  }

  function startEditBlock(block) {
    // Parse existing title to extract custom_label if not linked to a project
    const customLabel = !block.program_id ? block.title : "";
    setBlockForm({
      artist_id: block.artist_id,
      program_id: block.program_id || "",
      custom_label: customLabel,
      work_type: block.work_type,
      start_date: block.start_date.split("T")[0],
      end_date: block.end_date.split("T")[0],
      status: block.status,
      notes: block.notes || "",
      color: block.color || "",
      hours_type: block.hours_type || "full",
      hours_per_day: block.hours_per_day || 8,
      planner_item_id: block.planner_item_id || "",
      planner_item_type: block.planner_item_type || "",
      planner_item_label: block.planner_item_label || ""
    });
    // Load planner items if program has a planner
    if (block.program_id) {
      fetchPlannerItems(block.program_id);
    }
    setEditingBlock(block);
    setShowAddBlock(true);
  }
  
  // Fetch planner items when project is selected
  async function fetchPlannerItems(programId) {
    if (!programId) {
      setPlannerItems({ has_planner: false, items: [] });
      return;
    }
    setLoadingPlannerItems(true);
    try {
      const res = await apiClient.get(`/production-planners/by-program/${programId}/assignable-items`);
      setPlannerItems(res.data);
    } catch {
      // No planner for this program, that's okay
      setPlannerItems({ has_planner: false, items: [] });
    } finally {
      setLoadingPlannerItems(false);
    }
  }

  // Time Off CRUD
  async function handleSaveTimeOff() {
    if (!timeOffForm.artist_id || !timeOffForm.start_date || !timeOffForm.end_date) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      if (editingTimeOff) {
        const res = await apiClient.patch(`/time-off/${editingTimeOff.id}`, timeOffForm);
        setTimeOffs(timeOffs.map(t => t.id === editingTimeOff.id ? res.data : t));
        toast.success("Time off updated");
      } else {
        const res = await apiClient.post("/time-off", timeOffForm);
        setTimeOffs([...timeOffs, res.data]);
        toast.success("Time off added");
      }
      resetTimeOffForm();
    } catch (error) {
      toast.error("Failed to save time off");
    }
  }

  async function handleDeleteTimeOff(id) {
    try {
      await apiClient.delete(`/time-off/${id}`);
      setTimeOffs(timeOffs.filter(t => t.id !== id));
      toast.success("Time off deleted");
    } catch (error) {
      toast.error("Failed to delete time off");
    }
  }

  function resetTimeOffForm() {
    setTimeOffForm({ artist_id: "", type: "PTO", start_date: "", end_date: "", notes: "" });
    setShowTimeOff(false);
    setEditingTimeOff(null);
  }

  function startEditTimeOff(timeOff) {
    setTimeOffForm({
      artist_id: timeOff.artist_id,
      type: timeOff.type || "PTO",
      start_date: timeOff.start_date || "",
      end_date: timeOff.end_date || "",
      notes: timeOff.notes || ""
    });
    setEditingTimeOff(timeOff);
    setShowTimeOff(true);
  }

  // Drag and Drop handlers
  function handleDragStart(e, block) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", JSON.stringify({
      blockId: block.id,
      artistId: block.artist_id,
      startDate: block.start_date,
      endDate: block.end_date
    }));
    setDraggingBlock(block);
    // Add drag image
    const dragElement = e.target.cloneNode(true);
    dragElement.style.opacity = "0.8";
    document.body.appendChild(dragElement);
    e.dataTransfer.setDragImage(dragElement, 0, 0);
    setTimeout(() => document.body.removeChild(dragElement), 0);
  }

  function handleDragEnd() {
    setDraggingBlock(null);
    setDragOverDate(null);
    setDragOverArtist(null);
  }

  function handleDragOver(e, date, artistId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const dateStr = date.toISOString().split("T")[0];
    if (dragOverDate !== dateStr || dragOverArtist !== artistId) {
      setDragOverDate(dateStr);
      setDragOverArtist(artistId);
    }
  }

  function handleDragLeave() {
    // Small delay to prevent flickering
    setTimeout(() => {
      setDragOverDate(null);
      setDragOverArtist(null);
    }, 50);
  }

  // Reverse sync: when a booking that originated in the ArchViz Planner is edited or
  // deleted here, write the change back onto the originating planner phase (two-way).
  // Best-effort and non-blocking — a failure never disrupts the Artist Timeline edit.
  async function syncPlannerBack(block, { start_date, end_date, deleted } = {}) {
    if (!block || block.origin !== "archviz-planner" || !block.planner_item_id) return;
    try {
      await apiClient.post("/archviz/apply-assignment-edit", {
        planner_item_id: block.planner_item_id,
        start_date,
        end_date,
        deleted: !!deleted,
      });
    } catch (err) {
      console.warn("ArchViz reverse-sync skipped:", err);
    }
  }

  async function handleDrop(e, targetDate, targetArtistId) {
    e.preventDefault();
    setDragOverDate(null);
    setDragOverArtist(null);
    
    if (!draggingBlock) return;
    
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      const block = assignments.find(a => a.id === data.blockId);
      if (!block) return;
      
      // Calculate the working days duration of the block (excluding weekends)
      const originalStart = parseLocalDate(block.start_date);
      const originalEnd = parseLocalDate(block.end_date);
      if (!originalStart || !originalEnd) return;
      
      const workingDaysDuration = countWorkingDays(originalStart, originalEnd);
      
      // Calculate new dates - ensure start is a working day
      let newStart = new Date(targetDate);
      if (isWeekend(newStart)) {
        newStart = getNextWorkingDay(newStart);
      }
      
      // Add working days to get the end date (excluding weekends)
      const newEnd = addWorkingDays(newStart, workingDaysDuration);
      
      const newStartStr = newStart.toISOString().split("T")[0];
      const newEndStr = newEnd.toISOString().split("T")[0];
      
      // Check if anything actually changed
      const artistChanged = targetArtistId !== block.artist_id;
      const dateChanged = newStartStr !== block.start_date;
      
      if (!artistChanged && !dateChanged) {
        setDraggingBlock(null);
        return;
      }
      
      // Prepare update payload
      const updatePayload = {
        start_date: newStartStr,
        end_date: newEndStr
      };
      
      // If moving to different artist
      if (artistChanged) {
        updatePayload.artist_id = targetArtistId;
      }
      
      // Update the assignment
      const res = await apiClient.patch(`/assignment-blocks/${block.id}`, updatePayload);
      
      // Update local state
      setAssignments(assignments.map(a => a.id === block.id ? res.data : a));
      // Push the reschedule back to the originating planner phase, if any.
      syncPlannerBack(block, { start_date: newStartStr, end_date: newEndStr });

      const actionDesc = artistChanged
        ? `Moved "${block.title}" to new artist` 
        : `Rescheduled "${block.title}" to ${newStartStr}`;
      toast.success(actionDesc);
      
    } catch (error) {
      console.error("Drop error:", error);
      toast.error("Failed to move assignment");
    } finally {
      setDraggingBlock(null);
    }
  }

  // Resize handlers
  function handleResizeStart(e, block, edge) {
    e.preventDefault();
    e.stopPropagation();
    
    // Store in ref for event handlers
    resizeStateRef.current = {
      block: block,
      edge: edge,
      startX: e.clientX,
      originalBlock: { ...block }
    };
    
    setResizingBlock(block);
    setResizeEdge(edge);
    setResizeStartX(e.clientX);
    setResizeOriginalBlock({ ...block });
    
    // Add global mouse handlers
    const handleMove = (moveEvent) => {
      const state = resizeStateRef.current;
      if (!state.block || !state.originalBlock || !timelineRef.current) return;
      
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const dayWidth = timelineRect.width / viewDates.length;
      const deltaX = moveEvent.clientX - state.startX;
      const daysDelta = Math.round(deltaX / dayWidth);
      
      const originalStart = parseLocalDate(state.originalBlock.start_date);
      const originalEnd = parseLocalDate(state.originalBlock.end_date);
      if (!originalStart || !originalEnd) return;
      
      let newStart = new Date(originalStart);
      let newEnd = new Date(originalEnd);
      
      if (state.edge === "start") {
        // Calculate new start by adding/subtracting working days
        if (daysDelta > 0) {
          // Moving start forward - add working days
          for (let i = 0; i < daysDelta; i++) {
            newStart.setDate(newStart.getDate() + 1);
            while (isWeekend(newStart)) newStart.setDate(newStart.getDate() + 1);
          }
        } else if (daysDelta < 0) {
          // Moving start backward - subtract working days
          for (let i = 0; i < Math.abs(daysDelta); i++) {
            newStart.setDate(newStart.getDate() - 1);
            while (isWeekend(newStart)) newStart.setDate(newStart.getDate() - 1);
          }
        }
        // Ensure start doesn't go past end and is a working day
        if (newStart >= newEnd) {
          newStart = new Date(newEnd);
          newStart.setDate(newStart.getDate() - 1);
          while (isWeekend(newStart)) newStart.setDate(newStart.getDate() - 1);
        }
      } else {
        // Calculate new end by adding/subtracting working days
        if (daysDelta > 0) {
          // Moving end forward - add working days
          for (let i = 0; i < daysDelta; i++) {
            newEnd.setDate(newEnd.getDate() + 1);
            while (isWeekend(newEnd)) newEnd.setDate(newEnd.getDate() + 1);
          }
        } else if (daysDelta < 0) {
          // Moving end backward - subtract working days
          for (let i = 0; i < Math.abs(daysDelta); i++) {
            newEnd.setDate(newEnd.getDate() - 1);
            while (isWeekend(newEnd)) newEnd.setDate(newEnd.getDate() - 1);
          }
        }
        // Ensure end doesn't go before start and is a working day
        if (newEnd <= newStart) {
          newEnd = new Date(newStart);
          newEnd.setDate(newEnd.getDate() + 1);
          while (isWeekend(newEnd)) newEnd.setDate(newEnd.getDate() + 1);
        }
      }
      
      // Update local state for visual feedback
      const newStartStr = newStart.toISOString().split("T")[0];
      const newEndStr = newEnd.toISOString().split("T")[0];
      
      setAssignments(prev => prev.map(a => 
        a.id === state.block.id 
          ? { ...a, start_date: newStartStr, end_date: newEndStr }
          : a
      ));
    };
    
    const handleUp = async () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      
      const state = resizeStateRef.current;
      if (!state.block || !state.originalBlock) {
        setResizingBlock(null);
        setResizeEdge(null);
        setResizeOriginalBlock(null);
        resizeStateRef.current = { block: null, edge: null, startX: 0, originalBlock: null };
        return;
      }
      
      // Get current state of assignments to find the updated block
      setAssignments(prev => {
        const currentBlock = prev.find(a => a.id === state.block.id);
        if (!currentBlock) return prev;
        
        // Check if dates actually changed
        if (currentBlock.start_date === state.originalBlock.start_date && 
            currentBlock.end_date === state.originalBlock.end_date) {
          return prev;
        }
        
        // Make API call to persist the change
        apiClient.patch(`/assignment-blocks/${state.block.id}`, {
          start_date: currentBlock.start_date,
          end_date: currentBlock.end_date
        }).then(res => {
          setAssignments(p => p.map(a => a.id === state.block.id ? res.data : a));
          syncPlannerBack(state.block, { start_date: currentBlock.start_date, end_date: currentBlock.end_date });
          toast.success(`Resized "${state.block.title}" to ${currentBlock.start_date} - ${currentBlock.end_date}`);
        }).catch(error => {
          console.error("Resize error:", error);
          // Revert to original
          setAssignments(p => p.map(a => 
            a.id === state.block.id ? state.originalBlock : a
          ));
          toast.error("Failed to resize assignment");
        });
        
        return prev;
      });
      
      setResizingBlock(null);
      setResizeEdge(null);
      setResizeOriginalBlock(null);
      resizeStateRef.current = { block: null, edge: null, startX: 0, originalBlock: null };
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }

  // Navigation
  function navigateDate(direction) {
    const newDate = new Date(currentDate);
    if (viewMode === "day") newDate.setDate(newDate.getDate() + direction);
    else if (viewMode === "week") newDate.setDate(newDate.getDate() + (7 * direction));
    else newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  }

  // Get dates for current view
  const viewDates = useMemo(() => {
    if (viewMode === "day") return [new Date(currentDate)];
    if (viewMode === "week") return getWeekDates(currentDate);
    return getMonthDates(currentDate);
  }, [currentDate, viewMode]);

  // Helper function to check if artist is free during view period
  const isArtistFreeInPeriod = (artistId, viewStart, viewEnd) => {
    const artistAssignments = assignments.filter(a => a.artist_id === artistId);
    
    for (const block of artistAssignments) {
      const blockStart = parseLocalDate(block.start_date);
      const blockEnd = parseLocalDate(block.end_date);
      if (!blockStart || !blockEnd) continue;
      
      // Check if block overlaps with view period
      if (blockStart <= viewEnd && blockEnd >= viewStart) {
        return false; // Artist has assignment during this period
      }
    }
    return true; // No assignments found in the period
  };

  // Filter artists
  const filteredArtists = useMemo(() => {
    let result = artists.filter(a => a.status === "Active");
    
    if (selectedArtist) {
      result = artists.filter(a => a.id === selectedArtist);
    }
    
    // Apply "Free Artists" filter
    if (showFreeOnly && viewDates.length > 0) {
      const viewStart = viewDates[0];
      const viewEnd = viewDates[viewDates.length - 1];
      result = result.filter(a => isArtistFreeInPeriod(a.id, viewStart, viewEnd));
    }
    
    // Apply custom order if available
    if (artistOrder.length > 0) {
      result = [...result].sort((a, b) => {
        const aIndex = artistOrder.indexOf(a.id);
        const bIndex = artistOrder.indexOf(b.id);
        // Artists not in the order list go to the end
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }
    
    return result;
  }, [artists, selectedArtist, showFreeOnly, viewDates, assignments, artistOrder]);

  // Get block color based on color mode
  function getBlockColor(block) {
    if (colorMode === "pm") {
      // Find the program to get PM info
      const program = programs.find(p => p.id === block.program_id);
      const pmName = program?.primary_pm_name || program?.program_manager_name || "Unassigned";
      return stringToColor(pmName);
    }
    if (colorMode === "artist") {
      return stringToColor(block.artist_name || block.artist_id);
    }
    if (colorMode === "project") {
      return block.program_name ? stringToColor(block.program_name) : "#565350";
    }
    return WORK_TYPE_COLORS[block.work_type] || "#565350";
  }

  // Get PM name for a block (used in tooltips)
  function getBlockPMName(block) {
    const program = programs.find(p => p.id === block.program_id);
    return program?.primary_pm_name || program?.program_manager_name || "Unassigned";
  }

  // Calculate block positions with overlap detection for double-booking visualization
  function calculateBlockRows(blocks, viewDates) {
    if (!blocks.length || !viewDates.length) return [];
    
    const viewStart = viewDates[0];
    const viewEnd = viewDates[viewDates.length - 1];
    
    // Sort blocks by start date
    const sortedBlocks = [...blocks].sort((a, b) => {
      const aStart = parseLocalDate(a.start_date);
      const bStart = parseLocalDate(b.start_date);
      return (aStart?.getTime() || 0) - (bStart?.getTime() || 0);
    });
    
    // Assign rows to blocks, detecting overlaps
    const blockRows = [];
    const occupiedRows = []; // Array of { end: Date, row: number }
    
    for (const block of sortedBlocks) {
      const blockStart = parseLocalDate(block.start_date);
      const blockEnd = parseLocalDate(block.end_date);
      if (!blockStart || !blockEnd) continue;
      
      // Find the first available row
      let assignedRow = 0;
      for (let row = 0; row < 10; row++) {
        const rowOccupied = occupiedRows.some(occ => 
          occ.row === row && occ.end >= blockStart
        );
        if (!rowOccupied) {
          assignedRow = row;
          break;
        }
        assignedRow = row + 1;
      }
      
      // Mark this row as occupied until block ends
      occupiedRows.push({ end: blockEnd, row: assignedRow });
      
      // Check if this block overlaps with any other block (for warning)
      const hasOverlap = sortedBlocks.some(other => {
        if (other.id === block.id) return false;
        const otherStart = parseLocalDate(other.start_date);
        const otherEnd = parseLocalDate(other.end_date);
        if (!otherStart || !otherEnd) return false;
        return blockStart <= otherEnd && blockEnd >= otherStart;
      });
      
      blockRows.push({
        ...block,
        row: assignedRow,
        hasOverlap
      });
    }
    
    return blockRows;
  }

  // Capacity calculation
  const capacityOverview = useMemo(() => {
    const today = new Date();
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    
    return filteredArtists.map(artist => {
      const artistAssignments = assignments.filter(a => a.artist_id === artist.id);
      const artistTimeOff = timeOffs.filter(t => t.artist_id === artist.id);
      
      // Simple calculation for display
      const assignedDays = artistAssignments.length * 3; // Rough estimate
      const availableDays = 5;
      const utilization = Math.min(100, (assignedDays / availableDays) * 100);
      
      return {
        ...artist,
        assignmentCount: artistAssignments.length,
        utilization: Math.round(utilization),
        isOverAllocated: utilization > 100
      };
    });
  }, [filteredArtists, assignments, timeOffs]);

  // DnD sensors for artist row reordering
  const rowSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle artist row drag end
  function handleArtistDragEnd(event) {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = filteredArtists.findIndex(a => a.id === active.id);
      const newIndex = filteredArtists.findIndex(a => a.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Create new order based on current filtered view
        const newFilteredOrder = arrayMove(filteredArtists, oldIndex, newIndex);
        const newOrder = newFilteredOrder.map(a => a.id);
        
        // Merge with existing order for artists not currently visible
        const existingOrder = artistOrder.filter(id => !newOrder.includes(id));
        const fullOrder = [...newOrder, ...existingOrder];
        
        setArtistOrder(fullOrder);
        localStorage.setItem('artistTimelineOrder', JSON.stringify(fullOrder));
        toast.success("Artist order updated");
      }
    }
  }

  // Reset artist order
  function resetArtistOrder() {
    setArtistOrder([]);
    localStorage.removeItem('artistTimelineOrder');
    toast.success("Artist order reset to default");
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="artist-management">
      {/* Compact Header Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            Artists Timeline
          </h1>
          {/* Inline Stats - Apple-esque pill style */}
          <div className="hidden md:flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-secondary/60 rounded-full">
              <Users className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium">{artists.filter(a => a.status === "Active").length}</span>
              <span className="text-muted-foreground text-xs">artists</span>
            </span>
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-secondary/60 rounded-full">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium">{assignments.filter(a => a.status !== "On Hold").length}</span>
              <span className="text-muted-foreground text-xs">blocks</span>
            </span>
            {capacityOverview.filter(a => a.isOverAllocated).length > 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 rounded-full">
                <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
                <span className="font-medium text-orange-600">{capacityOverview.filter(a => a.isOverAllocated).length}</span>
                <span className="text-orange-600/70 text-xs">over</span>
              </span>
            )}
            {timeOffs.length > 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-full">
                <Clock className="w-3.5 h-3.5 text-blue-600" />
                <span className="font-medium text-blue-600">{timeOffs.length}</span>
                <span className="text-blue-600/70 text-xs">leave</span>
              </span>
            )}
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 rounded-full">
              <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span className="font-medium text-emerald-600">
                {(() => {
                  if (viewDates.length === 0) return 0;
                  const viewStart = viewDates[0];
                  const viewEnd = viewDates[viewDates.length - 1];
                  return artists.filter(a => a.status === "Active" && isArtistFreeInPeriod(a.id, viewStart, viewEnd)).length;
                })()}
              </span>
              <span className="text-emerald-600/70 text-xs">free</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {artistOrder.length > 0 && (
            <Button variant="ghost" size="sm" onClick={resetArtistOrder} title="Reset artist order to default">
              <GripVertical className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Reset Order</span>
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => syncLeavesToTimeline(true)}
            disabled={syncing}
            title="Sync approved leaves to timeline"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync Leaves'}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowTimeOff(true)}>
            <Clock className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Time Off</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/studio/resource-center")}>
            <BookOpen className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Resource Center</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { setShowOTList(true); fetchOTEntries(); }}
            data-testid="view-ot-btn"
          >
            <Clock className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">View OT</span>
          </Button>
          <Button 
            size="sm" 
            variant="secondary"
            onClick={() => setShowAddOT(true)} 
            data-testid="add-ot-btn"
            className="bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add OT
          </Button>
          <Button size="sm" onClick={() => setShowAddBlock(true)} data-testid="add-block-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Block
          </Button>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-secondary rounded-lg p-1">
            {["day", "week", "month"].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === mode ? "bg-white shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" onClick={() => setCurrentDate(new Date())} className="text-sm">
              Today
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigateDate(1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <span className="text-sm font-medium">
            {viewMode === "month" 
              ? currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
              : viewMode === "week"
              ? `${formatDate(viewDates[0])} - ${formatDate(viewDates[6])}`
              : formatDateFull(currentDate)
            }
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Artist Filter */}
          <Select value={selectedArtist || "all"} onValueChange={v => setSelectedArtist(v === "all" ? null : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Artists" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Artists</SelectItem>
              {artists.map(a => (
                <SelectItem key={a.id} value={a.id}>{getArtistDisplayName(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Free Artists Filter */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/50 rounded-lg">
            <Switch
              id="free-artists"
              checked={showFreeOnly}
              onCheckedChange={setShowFreeOnly}
              data-testid="free-artists-toggle"
            />
            <Label htmlFor="free-artists" className="text-sm font-medium cursor-pointer">
              Free Only
            </Label>
            {showFreeOnly && (
              <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">
                {filteredArtists.length}
              </Badge>
            )}
          </div>
          
          {/* Color Mode */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Palette className="w-4 h-4 mr-2" />
                Color
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {COLOR_MODES.map(mode => (
                <DropdownMenuItem
                  key={mode.id}
                  onClick={() => setColorMode(mode.id)}
                  className={colorMode === mode.id ? "bg-secondary" : ""}
                >
                  {colorMode === mode.id && <Check className="w-4 h-4 mr-2" />}
                  {mode.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Calendar Grid */}
      <Card className="border-border overflow-hidden">
        <CardContent className="p-0">
          {/* Header Row with Dates - Identical structure to body for pixel-perfect alignment */}
          <div className="flex border-b border-border bg-secondary/30">
            {/* Drag handle spacer - matches body drag handle width (w-6 = 24px) */}
            <div className="w-6 shrink-0 border-r border-border bg-muted/20" />
            {/* Left column - exactly matches body left column width */}
            <div className="w-48 md:w-56 shrink-0 p-3 border-r border-border font-medium text-sm box-border">
              Artist
            </div>
            {/* Date grid - matches body grid exactly */}
            <div 
              className="flex-1 grid box-border" 
              ref={timelineRef}
              style={{ 
                gridTemplateColumns: `repeat(${viewDates.length}, minmax(0, 1fr))`,
                paddingRight: scrollbarWidth > 0 ? `${scrollbarWidth}px` : 0 // Compensate for body scrollbar
              }}
            >
              {viewDates.map((date, idx) => {
                const isToday = isSameDay(date, new Date());
                const isWeekendDay = date.getDay() === 0 || date.getDay() === 6;
                // Check if this date is a holiday
                const dateStr = date.toISOString().split('T')[0];
                const holiday = holidays.find(h => {
                  const start = h.start_date;
                  const end = h.end_date;
                  return dateStr >= start && dateStr <= end;
                });
                return (
                  <div
                    key={idx}
                    className={`p-2 text-center text-xs border-r border-border last:border-r-0 box-border ${
                      holiday ? "bg-amber-100 dark:bg-amber-900/30" : isWeekendDay ? "bg-zinc-400/50 dark:bg-zinc-600/50" : ""
                    } ${isToday && !isWeekendDay ? "bg-blue-100 dark:bg-blue-900/30" : ""}`}
                    title={holiday ? `${holiday.name} (${holiday.leave_type === "national_holiday" ? "National" : "Company"} Holiday)` : isWeekendDay ? "Weekend" : ""}
                  >
                    <div className={`font-medium ${isWeekendDay ? "text-zinc-600 dark:text-zinc-300" : ""}`}>{date.toLocaleDateString("en-US", { weekday: "short" })}</div>
                    <div className={`text-lg ${isToday && !isWeekendDay ? "text-blue-600 dark:text-blue-400 font-bold" : ""} ${isWeekendDay ? "text-zinc-600 dark:text-zinc-300" : ""}`}>
                      {date.getDate()}
                    </div>
                    {holiday && (
                      <div className="text-[9px] text-amber-600 dark:text-amber-400 truncate" title={holiday.name}>
                        🎉 {holiday.name.substring(0, 8)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Artist Rows */}
          <DndContext
            sensors={rowSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleArtistDragEnd}
          >
            <SortableContext
              items={filteredArtists.map(a => a.id)}
              strategy={verticalListSortingStrategy}
            >
              <div ref={bodyScrollRef} className="max-h-[600px] overflow-y-auto relative">
                {/* Weekend Background Layer - Spans full body height */}
                <div 
                  className="absolute inset-0 pointer-events-none flex"
                  style={{ zIndex: 0 }}
                >
                  {/* Drag handle spacer width */}
                  <div className="w-6 shrink-0" />
                  {/* Artist name column width */}
                  <div className="w-48 md:w-56 shrink-0" />
                  {/* Day columns - full height weekend backgrounds */}
                  <div 
                    className="flex-1 grid"
                    style={{ gridTemplateColumns: `repeat(${viewDates.length}, minmax(0, 1fr))` }}
                  >
                    {viewDates.map((date, idx) => {
                      const isWeekendDay = date.getDay() === 0 || date.getDay() === 6;
                      const dateStr = date.toISOString().split('T')[0];
                      const holiday = holidays.find(h => {
                        const start = h.start_date;
                        const end = h.end_date;
                        return dateStr >= start && dateStr <= end;
                      });
                      return (
                        <div
                          key={`weekend-bg-${idx}`}
                          className={`h-full ${
                            holiday 
                              ? "bg-amber-100/70 dark:bg-amber-900/30" 
                              : isWeekendDay 
                                ? "bg-zinc-300/60 dark:bg-zinc-600/40" 
                                : ""
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
                
                {/* Artist Rows - Positioned above weekend background */}
                {filteredArtists.map(artist => {
                  const artistAssignments = assignments.filter(a => a.artist_id === artist.id);
                  const artistTimeOff = timeOffs.filter(t => t.artist_id === artist.id);
                  const cap = capacityOverview.find(c => c.id === artist.id);
                  
                  // Get blocks that overlap with current view range
                  const viewStart = viewDates[0];
                  const viewEnd = viewDates[viewDates.length - 1];
                  
                  const visibleBlocks = artistAssignments.filter(block => {
                    const blockStart = parseLocalDate(block.start_date);
                    const blockEnd = parseLocalDate(block.end_date);
                    if (!blockStart || !blockEnd) return false;
                    return blockStart <= viewEnd && blockEnd >= viewStart;
                  });
                  
                  // Calculate block rows with overlap detection
                  const blocksWithRows = calculateBlockRows(visibleBlocks, viewDates);
                  const maxRow = blocksWithRows.length > 0 ? Math.max(...blocksWithRows.map(b => b.row)) : 0;
                  const hasDoubleBooking = blocksWithRows.some(b => b.hasOverlap);
                  
                  const visibleTimeOff = artistTimeOff.filter(to => {
                    const toStart = parseLocalDate(to.start_date);
                    const toEnd = parseLocalDate(to.end_date);
                    if (!toStart || !toEnd) return false;
                    return toStart <= viewEnd && toEnd >= viewStart;
                  });
                  
                  // Get approved OT entries for this artist in the visible range
                  const artistOTEntries = otEntries.filter(ot => 
                    ot.artist_id === artist.id || ot.resource_id === artist.id
                  );
                  const visibleOT = artistOTEntries.filter(ot => {
                    const otDate = parseLocalDate(ot.date);
                    if (!otDate) return false;
                    return otDate >= viewStart && otDate <= viewEnd;
                  });
                  
                  return (
                    <SortableArtistRow key={artist.id} artist={artist}>
                      {/* Artist Info */}
                      <div className="w-48 md:w-56 shrink-0 p-3 border-r border-border">
                        <div className="flex items-start justify-between">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p 
                                className="font-medium text-sm truncate cursor-pointer hover:text-primary hover:underline"
                                onClick={() => navigate(`/studio/resources/${artist.id}`)}
                                title="View artist profile"
                              >
                                {getArtistDisplayName(artist)}
                              </p>
                              {hasDoubleBooking && (
                            <span title="Double-booked during this period">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{artist.role}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/studio/resources/${artist.id}`)}>
                            <User className="w-4 h-4 mr-2" /> View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setTimeOffForm({ ...timeOffForm, artist_id: artist.id });
                              setShowTimeOff(true);
                            }}
                          >
                            <Clock className="w-4 h-4 mr-2" /> Add Time Off
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Calendar Cells - Gantt Style - Reduced row height, expands for double-booking */}
                  <div className="flex-1 relative bg-transparent" style={{ minHeight: `${Math.max(40, (maxRow + 1) * 28 + 8)}px` }}>
                    {/* Grid Background - Drop Zones - Uses identical CSS Grid as header for pixel-perfect alignment */}
                    <div 
                      className="absolute inset-0 grid box-border"
                      style={{ gridTemplateColumns: `repeat(${viewDates.length}, minmax(0, 1fr))` }}
                    >
                      {viewDates.map((date, idx) => {
                        const isWeekendDay = date.getDay() === 0 || date.getDay() === 6;
                        const isToday = isSameDay(date, new Date());
                        const dateStr = date.toISOString().split("T")[0];
                        const isDropTarget = draggingBlock && dragOverDate === dateStr && dragOverArtist === artist.id;
                        
                        return (
                          <div
                            key={idx}
                            className={`border-r border-border/50 last:border-r-0 transition-colors duration-75 box-border ${
                              isWeekendDay ? "" : "cursor-pointer"
                            } ${isToday && !isWeekendDay ? "bg-blue-100/50 dark:bg-blue-900/20" : ""} ${
                              isDropTarget ? "bg-blue-200/60 ring-2 ring-blue-400 ring-inset" : !isWeekendDay ? "hover:bg-secondary/20" : ""
                            } ${draggingBlock && !isWeekendDay ? "hover:bg-blue-100/40" : ""}`}
                            onClick={() => {
                              if (!draggingBlock && !isWeekendDay) {
                                setBlockForm({
                                  ...blockForm,
                                  artist_id: artist.id,
                                  start_date: dateStr,
                                  end_date: dateStr
                                });
                                setShowAddBlock(true);
                              }
                            }}
                            onDragOver={(e) => !isWeekendDay && handleDragOver(e, date, artist.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => !isWeekendDay && handleDrop(e, date, artist.id)}
                          />
                        );
                      })}
                    </div>
                    
                    {/* Time Off Bars (background layer) */}
                    {visibleTimeOff.map((to, toIdx) => {
                      const toStart = parseLocalDate(to.start_date);
                      const toEnd = parseLocalDate(to.end_date);
                      if (!toStart || !toEnd) return null;
                      
                      // Calculate position using day index (more reliable than timestamps)
                      const numDays = viewDates.length;
                      
                      // Find start day index (clamp to view range)
                      let startIdx = viewDates.findIndex(d => isSameDay(d, toStart));
                      if (startIdx === -1) startIdx = toStart < viewStart ? 0 : numDays;
                      
                      // Find end day index (clamp to view range)
                      let endIdx = viewDates.findIndex(d => isSameDay(d, toEnd));
                      if (endIdx === -1) endIdx = toEnd > viewEnd ? numDays - 1 : -1;
                      
                      if (startIdx > numDays - 1 || endIdx < 0) return null;
                      
                      startIdx = Math.max(0, startIdx);
                      endIdx = Math.min(numDays - 1, endIdx);
                      
                      const startOffset = startIdx / numDays;
                      const endOffset = (endIdx + 1) / numDays;
                      const width = endOffset - startOffset;
                      
                      // Get color based on time-off type
                      const typeColors = {
                        "Sick": { bg: "bg-red-100", border: "border-red-500", text: "text-red-700", icon: "🤒" },
                        "PTO": { bg: "bg-blue-100", border: "border-blue-500", text: "text-blue-700", icon: "🏖️" },
                        "Personal": { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-700", icon: "📅" },
                        "Holiday": { bg: "bg-amber-100", border: "border-amber-500", text: "text-amber-700", icon: "🎉" },
                        "Other": { bg: "bg-amber-100", border: "border-amber-500", text: "text-amber-700", icon: "⏰" }
                      };
                      const colors = typeColors[to.type] || typeColors["Other"];
                      
                      return (
                        <div
                          key={`to-${to.id}`}
                          className={`absolute top-0 bottom-0 ${colors.bg} border-l-4 ${colors.border} cursor-pointer hover:opacity-80 z-30 transition-opacity`}
                          style={{
                            left: `${startOffset * 100}%`,
                            width: `${width * 100}%`
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditTimeOff(to);
                          }}
                          title={`${to.type}: ${formatDate(to.start_date)} - ${formatDate(to.end_date)}${to.notes ? `\n${to.notes}` : ''}`}
                        >
                          <div className="flex items-center h-full px-2 gap-1">
                            <span className="text-sm">{colors.icon}</span>
                            <span className={`text-xs font-semibold truncate ${colors.text}`}>
                              {to.type}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* OT Entries (approved overtime) */}
                    {visibleOT.map(ot => {
                      const otDate = parseLocalDate(ot.date);
                      if (!otDate) return null;
                      
                      const numDays = viewDates.length;
                      const dayIdx = viewDates.findIndex(d => isSameDay(d, otDate));
                      if (dayIdx === -1) return null;
                      
                      const startOffset = dayIdx / numDays;
                      const width = 1 / numDays;
                      
                      return (
                        <div
                          key={`ot-${ot.id}`}
                          className="absolute top-0 bg-orange-100 border-t-2 border-orange-500 cursor-pointer hover:bg-orange-200 z-25 transition-colors"
                          style={{
                            left: `${startOffset * 100}%`,
                            width: `${width * 100}%`,
                            height: '20px',
                            bottom: 'auto'
                          }}
                          title={`OT: ${ot.hours}h - ${ot.reason || 'Overtime'}`}
                        >
                          <div className="flex items-center h-full px-1 gap-0.5">
                            <span className="text-[9px]">⏰</span>
                            <span className="text-[9px] font-semibold text-orange-700 truncate">
                              +{ot.hours}h OT
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Assignment Blocks (foreground bars) - with visual weekend gaps */}
                    <div className="relative h-full p-1 z-20 pointer-events-none">
                      {blocksWithRows.map((block) => {
                        const blockStart = parseLocalDate(block.start_date);
                        const blockEnd = parseLocalDate(block.end_date);
                        if (!blockStart || !blockEnd) return null;
                        
                        const numDays = viewDates.length;
                        const blockColor = getBlockColor(block);
                        const isDragging = draggingBlock?.id === block.id;
                        const isResizing = resizingBlock?.id === block.id;
                        
                        // Split block into weekday segments for visual weekend gaps
                        const segments = splitBlockByWeekends(blockStart, blockEnd, viewDates);
                        
                        // Fallback: if no segments but block is in view range, show it without weekend splitting
                        if (segments.length === 0) {
                          // Check if block overlaps with view at all
                          const viewStart = viewDates[0];
                          const viewEnd = viewDates[viewDates.length - 1];
                          if (blockEnd < viewStart || blockStart > viewEnd) return null;
                          
                          // Render as single block (fallback for edge cases)
                          let startIdx = viewDates.findIndex(d => isSameDay(d, blockStart));
                          let endIdx = viewDates.findIndex(d => isSameDay(d, blockEnd));
                          if (startIdx === -1) startIdx = blockStart < viewStart ? 0 : numDays - 1;
                          if (endIdx === -1) endIdx = blockEnd > viewEnd ? numDays - 1 : 0;
                          startIdx = Math.max(0, startIdx);
                          endIdx = Math.min(numDays - 1, endIdx);
                          
                          const startOffset = startIdx / numDays;
                          const endOffset = (endIdx + 1) / numDays;
                          const width = endOffset - startOffset;
                          
                          // Calculate working days and total hours for tooltip
                          const workingDays = countWorkingDays(blockStart, blockEnd);
                          const hoursPerDay = block.hours_type === "half" ? 4 : block.hours_type === "custom" ? (block.hours_per_day || 8) : 8;
                          const totalHours = workingDays * hoursPerDay;
                          
                          // Generate display title: Project — WorkType or custom label
                          const displayTitle = block.program_id && block.program_name 
                            ? `${block.program_name} — ${block.work_type}`
                            : block.title;
                          
                          return (
                            <TooltipProvider key={block.id} delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={`absolute rounded shadow-sm flex items-center overflow-hidden pointer-events-auto hover:shadow-md ${
                                      block.hours_type === "half" ? "h-4" : "h-7"
                                    }`}
                                    style={{
                                      left: `${startOffset * 100}%`,
                                      width: `${width * 100}%`,
                                      top: `${4 + block.row * 32}px`,
                                      backgroundColor: blockColor + "25",
                                      borderLeft: `3px solid ${blockColor}`,
                                      minWidth: '20px',
                                      cursor: 'pointer'
                                    }}
                                    onClick={(e) => { e.stopPropagation(); startEditBlock(block); }}
                                  >
                                    <span className="text-[11px] font-medium truncate px-2" style={{ color: blockColor }}>
                                      {displayTitle}{block.hours_type === "half" ? " (½)" : ""}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent 
                                  side="top" 
                                  align="start"
                                  sideOffset={8}
                                  className="p-0 bg-zinc-900/95 dark:bg-zinc-950/95 backdrop-blur-xl border-zinc-700/50 shadow-xl rounded-lg"
                                >
                                  <BlockTooltipContent 
                                    block={block} 
                                    artistName={getArtistDisplayName(artist)}
                                    workingDays={workingDays}
                                    totalHours={totalHours}
                                  />
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        }
                        
                        return segments.map((segment, segIdx) => {
                          // Find segment day indices - normalize segment dates for comparison
                          const segStart = new Date(segment.start);
                          const segEnd = new Date(segment.end);
                          segStart.setHours(0, 0, 0, 0);
                          segEnd.setHours(0, 0, 0, 0);
                          
                          let segStartIdx = viewDates.findIndex(d => {
                            const viewDay = new Date(d);
                            viewDay.setHours(0, 0, 0, 0);
                            return viewDay.getTime() === segStart.getTime();
                          });
                          let segEndIdx = viewDates.findIndex(d => {
                            const viewDay = new Date(d);
                            viewDay.setHours(0, 0, 0, 0);
                            return viewDay.getTime() === segEnd.getTime();
                          });
                          
                          if (segStartIdx === -1 || segEndIdx === -1) return null;
                          
                          const startOffset = segStartIdx / numDays;
                          const endOffset = (segEndIdx + 1) / numDays;
                          const width = endOffset - startOffset;
                          
                          const isFirstSegment = segIdx === 0;
                          const isLastSegment = segIdx === segments.length - 1;
                          
                          // Calculate working days and total hours for tooltip
                          const workingDays = countWorkingDays(blockStart, blockEnd);
                          const hoursPerDay = block.hours_type === "half" ? 4 : block.hours_type === "custom" ? (block.hours_per_day || 8) : 8;
                          const totalHours = workingDays * hoursPerDay;
                          
                          // Generate display title: Project — WorkType or custom label
                          const displayTitle = block.program_id && block.program_name 
                            ? `${block.program_name} — ${block.work_type}`
                            : block.title;
                          
                          return (
                            <TooltipProvider key={`${block.id}-seg-${segIdx}`} delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    draggable={!isResizing && isFirstSegment}
                                    onDragStart={(e) => !isResizing && isFirstSegment && handleDragStart(e, block)}
                                    onDragEnd={handleDragEnd}
                                    className={`absolute shadow-sm flex items-center overflow-hidden pointer-events-auto group transition-all duration-100 ${
                                      block.hours_type === "half" ? "h-4" : "h-7"
                                    } ${
                                      isFirstSegment ? "rounded-l" : ""
                                    } ${isLastSegment ? "rounded-r" : ""} ${
                                      block.hasOverlap ? "ring-2 ring-amber-400 ring-offset-1" : ""
                                    } ${isDragging ? "opacity-50 scale-95 ring-2 ring-blue-500" : "hover:shadow-md"} ${
                                      isResizing ? "ring-2 ring-green-500 shadow-lg" : ""
                                    }`}
                                    style={{
                                      left: `${startOffset * 100}%`,
                                      width: `${width * 100}%`,
                                      top: `${4 + block.row * 32}px`,
                                      backgroundColor: blockColor + "25",
                                      borderLeft: isFirstSegment ? `3px solid ${blockColor}` : `1px dashed ${blockColor}40`,
                                      borderRight: isLastSegment ? `3px solid ${blockColor}` : `1px dashed ${blockColor}40`,
                                      minWidth: '20px',
                                      cursor: isResizing ? 'ew-resize' : (isFirstSegment ? 'grab' : 'pointer')
                                    }}
                                    onClick={(e) => { e.stopPropagation(); startEditBlock(block); }}
                                  >
                                    {/* Left Resize Handle - only on first segment */}
                                    {isFirstSegment && (
                                      <div
                                        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 hover:bg-blue-400/50 transition-opacity z-10"
                                        onMouseDown={(e) => handleResizeStart(e, block, "start")}
                                      />
                                    )}
                                    {/* Content - only show on first segment */}
                                    {isFirstSegment && (
                                      <>
                                        {/* Drag Handle */}
                                        <div className="flex items-center justify-center w-5 h-full opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" style={{ color: blockColor }}>
                                          <GripVertical className="w-3 h-3" />
                                        </div>
                                        {/* Block Content - Auto-generated: Project — WorkType */}
                                        <div className="flex-1 flex items-center min-w-0 pr-2 gap-1">
                                          {block.program_id && block.program_name ? (
                                            <>
                                              <a
                                                href={`/programs/${block.program_id}`}
                                                onClick={(e) => { e.stopPropagation(); navigate(`/programs/${block.program_id}`); }}
                                                className="text-[10px] font-semibold truncate hover:underline flex-shrink-0 max-w-[50%]"
                                                style={{ color: blockColor }}
                                              >
                                                {block.program_name}
                                              </a>
                                              <span className="text-[10px] opacity-50" style={{ color: blockColor }}>—</span>
                                              <span className="text-[10px] truncate opacity-80" style={{ color: blockColor }}>
                                                {block.work_type}
                                              </span>
                                            </>
                                          ) : (
                                            <span 
                                              className="text-[11px] font-medium truncate"
                                              style={{ color: blockColor }}
                                            >
                                              {block.title}
                                            </span>
                                          )}
                                          {block.hours_type === "half" && (
                                            <span className="text-[9px] opacity-70 font-medium ml-0.5" style={{ color: blockColor }}>½</span>
                                          )}
                                          {block.origin === "archviz-planner" && (
                                            <span
                                              title="Booked from the ArchViz Planner"
                                              className="text-[8px] font-bold px-1 rounded-sm ml-0.5 shrink-0"
                                              style={{ backgroundColor: "rgba(167,139,250,0.2)", color: "#8FA79B" }}
                                            >
                                              AV
                                            </span>
                                          )}
                                          {block.hasOverlap && (
                                            <AlertTriangle className="w-3 h-3 text-amber-500 ml-1 shrink-0" />
                                          )}
                                        </div>
                                      </>
                                    )}
                                    {/* Continuation indicator for non-first segments */}
                                    {!isFirstSegment && (
                                      <div className="flex items-center justify-center w-full h-full">
                                        <span className="text-[9px] opacity-60" style={{ color: blockColor }}>…</span>
                                      </div>
                                    )}
                                    {/* Right Resize Handle - only on last segment */}
                                    {isLastSegment && (
                                      <div
                                        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 hover:bg-blue-400/50 transition-opacity z-10"
                                        onMouseDown={(e) => handleResizeStart(e, block, "end")}
                                      />
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent 
                                  side="top" 
                                  align="start"
                                  sideOffset={8}
                                  className="p-0 bg-zinc-900/95 dark:bg-zinc-950/95 backdrop-blur-xl border-zinc-700/50 shadow-xl rounded-lg"
                                >
                                  <BlockTooltipContent 
                                    block={block} 
                                    artistName={getArtistDisplayName(artist)}
                                    workingDays={workingDays}
                                    totalHours={totalHours}
                                  />
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        });
                      })}
                    </div>
                  </div>
                </SortableArtistRow>
              );
            })}
            
            {filteredArtists.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>No artists found</p>
                <Button onClick={() => navigate("/studio/resource-center")} className="mt-4 rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> Go to Resource Center
                </Button>
              </div>
            )}
              </div>
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>

      {/* Work Type Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-4">
        <span className="text-xs text-muted-foreground">Work Types:</span>
        {Object.entries(WORK_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
            <span className="text-xs">{type}</span>
          </div>
        ))}
      </div>

      {/* Add/Edit Block Dialog */}
      <Dialog open={showAddBlock} onOpenChange={v => { if (!v) resetBlockForm(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>
              {editingBlock ? "Edit Assignment Block" : "Add Assignment Block"}
            </DialogTitle>
            <DialogDescription>
              Schedule work for an artist
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Artist (required) */}
            <div className="space-y-2">
              <Label>Artist *</Label>
              <Select value={blockForm.artist_id} onValueChange={v => setBlockForm({ ...blockForm, artist_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select artist" /></SelectTrigger>
                <SelectContent>
                  {artists.filter(a => a.status === "Active").map(a => (
                    <SelectItem key={a.id} value={a.id}>{getArtistDisplayName(a)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Link to Project - moved to top */}
            <div className="space-y-2">
              <Label>Link to Project {!blockForm.program_id && "*"}</Label>
              <Select 
                value={blockForm.program_id || "none"} 
                onValueChange={v => {
                  const newProgramId = v === "none" ? "" : v;
                  setBlockForm({ 
                    ...blockForm, 
                    program_id: newProgramId, 
                    custom_label: v !== "none" ? "" : blockForm.custom_label,
                    planner_item_id: "",
                    planner_item_type: "",
                    planner_item_label: ""
                  });
                  if (newProgramId) {
                    fetchPlannerItems(newProgramId);
                  } else {
                    setPlannerItems({ has_planner: false, items: [] });
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No project (use custom label) —</SelectItem>
                  {[...programs].sort((a, b) => (a.program_name || "").localeCompare(b.program_name || "", undefined, { sensitivity: "base" })).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {blockForm.program_id && (
                <p className="text-xs text-muted-foreground">
                  Block will display as: <span className="font-medium">{programs.find(p => p.id === blockForm.program_id)?.program_name} — {blockForm.work_type}</span>
                </p>
              )}
            </div>
            
            {/* Planner Item Drill-down - only shown if project has a production planner */}
            {blockForm.program_id && (
              <div className="space-y-2">
                {loadingPlannerItems ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Loading production planner...
                  </div>
                ) : plannerItems.has_planner ? (
                  <>
                    <Label className="flex items-center gap-2">
                      Link to Deliverable
                      <Badge variant="secondary" className="text-[10px]">
                        {plannerItems.summary?.total || 0} items
                      </Badge>
                    </Label>
                    <Select
                      value={blockForm.planner_item_id || "none"}
                      onValueChange={v => {
                        if (v === "none") {
                          setBlockForm({ 
                            ...blockForm, 
                            planner_item_id: "", 
                            planner_item_type: "", 
                            planner_item_label: "" 
                          });
                        } else {
                          const item = plannerItems.items.find(i => i.id === v);
                          if (item) {
                            setBlockForm({
                              ...blockForm,
                              planner_item_id: item.id,
                              planner_item_type: item.type,
                              planner_item_label: item.display_label,
                              // Auto-set work type based on category if visualization
                              work_type: item.category === "images" ? "Interior" : 
                                        item.category === "animations" ? "Animation" :
                                        item.type === "vexos" ? "Internal" : blockForm.work_type
                            });
                          }
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select deliverable (optional)" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectItem value="none">— Assign to project generally —</SelectItem>
                        {plannerItems.items.filter(i => i.type === "visualization").length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">
                              Visualization ({plannerItems.summary?.visualization_count})
                            </div>
                            {plannerItems.items
                              .filter(i => i.type === "visualization")
                              .map(item => (
                                <SelectItem key={item.id} value={item.id}>
                                  <div className="flex flex-col">
                                    <span>{item.display_label}</span>
                                    {item.status !== "not_started" && (
                                      <span className="text-[10px] text-muted-foreground">{item.status}</span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))
                            }
                          </>
                        )}
                        {plannerItems.items.filter(i => i.type === "vexos").length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">
                              VEXOS ({plannerItems.summary?.vexos_count})
                            </div>
                            {plannerItems.items
                              .filter(i => i.type === "vexos")
                              .map(item => (
                                <SelectItem key={item.id} value={item.id}>
                                  <div className="flex flex-col">
                                    <span>{item.display_label}</span>
                                    {item.status !== "not_started" && (
                                      <span className="text-[10px] text-muted-foreground">{item.status}</span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))
                            }
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    {blockForm.planner_item_id && (
                      <p className="text-xs text-green-600">
                        ✓ Linked to: {blockForm.planner_item_label}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    No production planner for this project. Assignment will link to project only.
                  </p>
                )}
              </div>
            )}
            
            {/* Custom Label - only shown if no project linked */}
            {!blockForm.program_id && (
              <div className="space-y-2">
                <Label>Custom Label *</Label>
                <Input
                  value={blockForm.custom_label}
                  onChange={e => setBlockForm({ ...blockForm, custom_label: e.target.value })}
                  placeholder="e.g., Internal Training, Meeting"
                  maxLength={60}
                />
                <p className="text-xs text-muted-foreground">{blockForm.custom_label.length}/60 characters</p>
              </div>
            )}
            
            {/* Work Type + Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Work Type</Label>
                <Select value={blockForm.work_type} onValueChange={v => setBlockForm({ ...blockForm, work_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(WORK_TYPE_COLORS).map(t => (
                      <SelectItem key={t} value={t}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: WORK_TYPE_COLORS[t] }} />
                          {t}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={blockForm.status} onValueChange={v => setBlockForm({ ...blockForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BLOCK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Start/End Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={blockForm.start_date}
                  onChange={e => setBlockForm({ ...blockForm, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={blockForm.end_date}
                  onChange={e => setBlockForm({ ...blockForm, end_date: e.target.value })}
                />
              </div>
            </div>
            
            {/* Hours per Day Selection */}
            <div className="space-y-2">
              <Label>Hours per Day</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={blockForm.hours_type === "half" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setBlockForm({ ...blockForm, hours_type: "half", hours_per_day: 4 })}
                >
                  Half Day (4h)
                </Button>
                <Button
                  type="button"
                  variant={blockForm.hours_type === "full" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setBlockForm({ ...blockForm, hours_type: "full", hours_per_day: 8 })}
                >
                  Full Day (8h)
                </Button>
                <Button
                  type="button"
                  variant={blockForm.hours_type === "custom" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setBlockForm({ ...blockForm, hours_type: "custom" })}
                >
                  Custom
                </Button>
              </div>
              {blockForm.hours_type === "custom" && (
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    step={0.5}
                    value={blockForm.hours_per_day}
                    onChange={e => setBlockForm({ ...blockForm, hours_per_day: parseFloat(e.target.value) || 8 })}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">hours/day</span>
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={blockForm.notes}
                onChange={e => setBlockForm({ ...blockForm, notes: e.target.value })}
                placeholder="Internal notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            {editingBlock && (
              <Button 
                variant="outline" 
                onClick={() => { handleDeleteBlock(editingBlock.id); resetBlockForm(); }}
                className="rounded-full text-destructive mr-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            )}
            <Button variant="outline" onClick={resetBlockForm} className="rounded-full">Cancel</Button>
            <Button onClick={handleSaveBlock} className="rounded-full">
              {editingBlock ? "Save Changes" : "Add Block"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Off Dialog */}
      <Dialog open={showTimeOff} onOpenChange={v => { if (!v) resetTimeOffForm(); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>
              {editingTimeOff ? "Edit Time Off" : "Add Time Off"}
            </DialogTitle>
            <DialogDescription>
              Block availability for an artist
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Artist *</Label>
              <Select value={timeOffForm.artist_id} onValueChange={v => setTimeOffForm({ ...timeOffForm, artist_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select artist" /></SelectTrigger>
                <SelectContent>
                  {artists.map(a => (
                    <SelectItem key={a.id} value={a.id}>{getArtistDisplayName(a)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={timeOffForm.type} onValueChange={v => setTimeOffForm({ ...timeOffForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIME_OFF_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={timeOffForm.start_date}
                  onChange={e => setTimeOffForm({ ...timeOffForm, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={timeOffForm.end_date}
                  onChange={e => setTimeOffForm({ ...timeOffForm, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={timeOffForm.notes}
                onChange={e => setTimeOffForm({ ...timeOffForm, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            {editingTimeOff && (
              <Button 
                variant="outline" 
                onClick={() => { handleDeleteTimeOff(editingTimeOff.id); resetTimeOffForm(); }}
                className="rounded-full text-destructive mr-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            )}
            <Button variant="outline" onClick={resetTimeOffForm} className="rounded-full">Cancel</Button>
            <Button onClick={handleSaveTimeOff} className="rounded-full">
              {editingTimeOff ? "Save Changes" : "Add Time Off"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add OT Modal */}
      <Dialog open={showAddOT} onOpenChange={setShowAddOT}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              {editingOT ? "Edit OT Entry" : "Add Overtime"}
            </DialogTitle>
            <DialogDescription>
              Log overtime hours for an artist. Default rate is $15/hr. OT will be recorded in the project's costing tab.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOT}>
            <div className="space-y-4 py-4">
              {/* Artist Selection */}
              <div className="space-y-2">
                <Label>Artist *</Label>
                <Select 
                  value={otForm.artist_id || ""} 
                  onValueChange={v => setOtForm({ ...otForm, artist_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select artist" /></SelectTrigger>
                  <SelectContent>
                    {artists.map(a => (
                      <SelectItem key={a.id} value={a.id}>{getArtistDisplayName(a)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={otForm.date}
                  onChange={e => setOtForm({ ...otForm, date: e.target.value })}
                  required
                />
              </div>

              {/* Hours and Rate - side by side */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>OT Hours *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={otForm.hours}
                    onChange={e => setOtForm({ ...otForm, hours: e.target.value })}
                    placeholder="e.g., 2"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>OT Rate ($/hr)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={otForm.rate}
                    onChange={e => setOtForm({ ...otForm, rate: e.target.value })}
                    placeholder="15"
                  />
                  <p className="text-[10px] text-muted-foreground">Default: $15/hr</p>
                </div>
              </div>

              {/* Live Total Calculation */}
              {otForm.hours && otForm.rate && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-800">Total OT Cost:</span>
                    <span className="text-lg font-bold text-amber-900">
                      ${(parseFloat(otForm.hours || 0) * parseFloat(otForm.rate || 15)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Project Selection */}
              <div className="space-y-2">
                <Label>Link to Project</Label>
                <Select 
                  value={otForm.program_id || "none"} 
                  onValueChange={v => {
                    const newProgramId = v === "none" ? "" : v;
                    setOtForm({ 
                      ...otForm, 
                      program_id: newProgramId,
                      deliverable_id: "",
                      deliverable_type: "",
                      deliverable_label: ""
                    });
                    if (newProgramId) {
                      fetchOtPlannerItems(newProgramId);
                    } else {
                      setOtPlannerItems([]);
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select project (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No project —</SelectItem>
                    {[...programs].sort((a, b) => (a.program_name || "").localeCompare(b.program_name || "", undefined, { sensitivity: "base" })).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Deliverable Drill-down (same as Add Block) */}
              {otForm.program_id && (
                <div className="space-y-2">
                  {loadingOtPlannerItems ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      Loading deliverables...
                    </div>
                  ) : otPlannerItems.length > 0 ? (
                    <>
                      <Label className="flex items-center gap-2">
                        Link to Deliverable
                        <Badge variant="secondary" className="text-[10px]">{otPlannerItems.length} items</Badge>
                      </Label>
                      <Select
                        value={otForm.deliverable_id || "none"}
                        onValueChange={v => {
                          if (v === "none") {
                            setOtForm({ 
                              ...otForm, 
                              deliverable_id: "", 
                              deliverable_type: "", 
                              deliverable_label: "" 
                            });
                          } else {
                            const item = otPlannerItems.find(i => i.id === v);
                            if (item) {
                              setOtForm({
                                ...otForm,
                                deliverable_id: item.id,
                                deliverable_type: item.type || item.category,
                                deliverable_label: item.display_label || item.name || item.title
                              });
                            }
                          }
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Select deliverable (optional)" /></SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          <SelectItem value="none">— Assign to project generally —</SelectItem>
                          {otPlannerItems.map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.display_label || item.name || item.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      No production planner deliverables for this project. OT will be assigned to project generally.
                    </p>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Reason / Notes</Label>
                <Textarea
                  value={otForm.notes}
                  onChange={e => setOtForm({ ...otForm, notes: e.target.value })}
                  placeholder="Why was overtime needed? (recommended)"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowAddOT(false);
                  setOtForm({
                    artist_id: "", program_id: "", deliverable_id: "", deliverable_type: "",
                    deliverable_label: "", date: "", hours: "", rate: 15, notes: ""
                  });
                  setOtPlannerItems([]);
                }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-full bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-1" />
                Save OT Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* OT Entries List Modal */}
      <Dialog open={showOTList} onOpenChange={setShowOTList}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Overtime Entries
            </DialogTitle>
            <DialogDescription>
              View and manage all overtime entries logged from Artist Timeline
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingOT ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : otEntries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No overtime entries logged yet</p>
                <p className="text-sm mt-2">Click "Add OT" to log overtime hours</p>
              </div>
            ) : (
              <div className="space-y-3 p-2">
                {otEntries.map(entry => (
                  <div 
                    key={entry.id} 
                    className="border rounded-lg p-4 hover:bg-secondary/30 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{entry.artist_name || "Unknown Artist"}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {new Date(entry.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </Badge>
                        </div>
                        {entry.program_name && (
                          <p className="text-sm text-muted-foreground">
                            Project: {entry.program_name}
                            {entry.deliverable_label && ` → ${entry.deliverable_label}`}
                          </p>
                        )}
                        {entry.notes && (
                          <p className="text-sm text-muted-foreground italic">{entry.notes}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Added by {entry.created_by_name || "Unknown"} on {new Date(entry.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right space-y-1">
                        <div className="text-lg font-bold text-amber-700">
                          ${entry.total_cost?.toFixed(2) || (entry.hours * entry.rate).toFixed(2)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {entry.hours}hrs @ ${entry.rate}/hr
                        </p>
                        <div className="flex gap-1 mt-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 text-xs"
                            onClick={() => handleDeleteOT(entry.id)}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowOTList(false)} className="rounded-full">
              Close
            </Button>
            <Button onClick={() => { setShowOTList(false); setShowAddOT(true); }} className="rounded-full bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-1" />
              Add New OT
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
